import { Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import RightSectionLogin from "../components/RightSectionLogin";
import { useCallback, useEffect, useState } from "react";
import PopUp, { PopupSize } from "components/Popup";
import ModalAuth from "components/Auth/ModalAuth";
import successIcon from "../assets/images/success_icon.svg";
import {
  SignBytesFailed,
  SignBytesResult,
  Timeout,
  useConnectedWallet,
  UserDenied,
  useWallet,
  verifyBytes,
  WalletStatus,
} from "@terra-money/wallet-provider";
import { useTranslation } from "react-i18next";
import SideMenu, { ModeType } from "../components/SideMenu";
import LoginSectionButtons from "components/Auth/LoginSectionButtons";
import { AuthFlow, ConnectWallet } from "components/Auth/ConnectWallet";
import { ModalState } from "./SignIn";
import classNames from "classnames";
import { instanceOfUserData } from "utils/instanceOf";
import { useContextUser } from "contexts/user";
import jscrypto from "jscrypto";
import { Warning } from "components/Modals";
import Loader, { LoaderType } from "components/Loader";

export enum LoginStep {
  connectWallet = "connectWallet",
  verifyWallet = "verifyWallet",
}

export const LoginPage: React.FC = () => {
  const { status, wallets, disconnect } = useWallet();
  const { t } = useTranslation(["common", "enumerations"]);
  const connectedWallet = useConnectedWallet();
  const { setUser } = useContextUser();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  let dataFromHandleSocials = state;

  const [width, setWidth] = useState(window.innerWidth);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState<null | ModalState>(null);
  const [currentStep, setCurrentStep] = useState(
    status === WalletStatus.WALLET_CONNECTED
      ? LoginStep.verifyWallet
      : LoginStep.connectWallet
  );
  const [randomBytes, setRandomBytes] = useState("");
  const [txError, setTxError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);

  const updateWidth = () => {
    setWidth(window.innerWidth);
  };

  const handleModal = (content: any) => {
    setIsOpenModal && setIsOpenModal(true);
    setModalContent(content);
  };

  const handleLoggedInUser = async (
    walletAddress: string,
    signBytesResult: any,
    originalBytes: any
  ) => {
    setIsLoading(true);
    try {
      const connection = await fetch(
        `${process.env.REACT_APP_MIDDLEWARE_NEAR_URL}/fauna/auth/login`,
        {
          method: "POST",
          body: JSON.stringify({
            walletAddress,
            signBytesResult,
            originalBytes,
          }),
          credentials: "include",
        }
      );
      const dataFromApi = await connection.json();
      if (
        typeof dataFromApi.data === "object" &&
        instanceOfUserData(dataFromApi.data)
      ) {
        setTempUserData(dataFromApi.data);
        handleModal(ModalState.success);
      } else {
        handleModal(ModalState.failure);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const signBytes = useCallback(
    async (originalBytes) => {
      if (!connectedWallet) {
        return;
      }
      try {
        const bufferedBytes = Buffer.from("test");
        /* Sign bytes with bytes fetched from API to this specific wallet */
        const signedBytes: SignBytesResult = await connectedWallet.signBytes(
          bufferedBytes
        );
        /* Send information from signing to API to verify the transaction */
        handleLoggedInUser(
          connectedWallet.terraAddress,
          signedBytes.result,
          originalBytes
        );
      } catch (error) {
        if (error instanceof UserDenied) {
          setTxError("User Denied");
        } else if (error instanceof Timeout) {
          setTxError("Timeout");
        } else if (error instanceof SignBytesFailed) {
          setTxError("Sign Bytes Failed");
        } else {
          setTxError(
            "Unknown Error: " +
              (error instanceof Error ? error.message : String(error))
          );
        }
        handleModal(ModalState.failure);
      }
    },
    [connectedWallet]
  );

  const handleWarningText = () => {
    if (txError) {
      return txError;
    } else {
      return t("modal_content.failure.modal_title", {
        ns: "enumerations",
      });
    }
  };

  const handleTryAgain = () => {
    setTimeout(() => {
      disconnect();
      setCurrentStep(LoginStep.connectWallet);
      setIsOpenModal(false);
      setModalContent(null);
    }, 3000);
  };

  const handleSetUser = () => {
    setTimeout(() => {
      console.log("in time out");
      setIsOpenModal(false);
      setModalContent(null);
      setUser(tempUserData);
    }, 3000);
  };

  const handleDisconnect = () => {
    disconnect();
    setIsOpenModal(false);
    setModalContent(null);
  };

  const handleSignup = () => {
    setIsOpenModal(false);
    setModalContent(null);
    navigate("/signup");
  };

  const handleCloseModal = () => {
    setTimeout(() => {
      setIsOpenModal(false);
      dataFromHandleSocials = null;
      setModalContent(null);
    }, 3000);
  };

  useEffect(() => {
    if (dataFromHandleSocials !== null) {
      setIsOpenModal(true);
      setModalContent(dataFromHandleSocials.modalContent);
      dataFromHandleSocials = null;
    }
  }, [dataFromHandleSocials]);

  useEffect(() => {
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  });

  useEffect(() => {
    if (currentStep === LoginStep.verifyWallet) {
      const verifyWallet = async () => {
        /* Upon intention to verify wallet, ask the API for bytes for that wallet */
        try {
          const connection = await fetch(
            `${process.env.REACT_APP_MIDDLEWARE_NEAR_URL}/fauna/auth/bytes`,
            {
              method: "POST",
              body: JSON.stringify({
                walletAddress: connectedWallet?.terraAddress,
              }),
              credentials: "include",
            }
          );
          const dataFromApi = await connection.json();
          setRandomBytes(dataFromApi.data.bytes);
          await signBytes(dataFromApi.data.bytes);
        } catch (error) {
          console.error(error);
        }
      };
      verifyWallet();
    }
  }, [currentStep]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const existUrlParams = window.location.search.includes("?");

    if (existUrlParams) {
      const accountId = urlParams.get("account_id");
      const loginUser = async () => {
        setIsLoading(true);
        try {
          const connection = await fetch(
            `${process.env.REACT_APP_MIDDLEWARE_NEAR_URL}/fauna/auth/login-near`,
            {
              method: "POST",
              body: JSON.stringify({
                walletAddress: accountId,
              }),
              credentials: "include",
            }
          );
          const dataFromApi = await connection.json();
          if (
            typeof dataFromApi.data === "object" &&
            instanceOfUserData(dataFromApi.data)
          ) {
            setTempUserData(dataFromApi.data);
            handleModal(ModalState.success);
          } else {
            handleModal(ModalState.failure);
          }
        } catch (error) {
          console.error(error);
        }
        setIsLoading(false);
      };
      loginUser();
    }
  }, []);

  return (
    <>
      {isLoading && <Loader type={LoaderType.fullScreen} />}
      <div className="flex flex-grow">
        <SideMenu authPage={true} mode={ModeType.wide}>
          <Logo />
          <h2 className="self-center text-lg md:text-xl">Log In</h2>
          <LoginSectionButtons
            walletStatus={status}
            onConnectWalletClick={() => setIsOpenModal(true)}
          />
          <p
            className={classNames(
              "static text-sm md:text-base self-center",
              { "mt-4": width < 360 },
              { "mt-14": width >= 360 }
            )}
          >
            Don't have an account yet?{" "}
            <NavLink to="/signup" className={"text-muddywaters-500"}>
              New Account
            </NavLink>
          </p>
        </SideMenu>
        <RightSectionLogin />
        {isOpenModal && (
          <>
            {modalContent === null && (
              <PopUp
                size={PopupSize.large}
                closeModal={() => setIsOpenModal(false)}
              >
                <div className="flex flex-col">
                  <ConnectWallet
                    setIsOpenModal={setIsOpenModal}
                    setModalContent={setModalContent}
                    modalContent={modalContent}
                    authFlow={AuthFlow.login}
                    setCurrentStep={setCurrentStep}
                  />
                </div>
              </PopUp>
            )}
            {modalContent === ModalState.success && (
              <PopUp size={PopupSize.large}>
                <ModalAuth
                  modalTitle={t(
                    "modal_content.connect_wallet_success.modal_title",
                    { ns: "enumerations" }
                  )}
                  modalImage={successIcon}
                >
                  {handleSetUser()}
                </ModalAuth>
              </PopUp>
            )}
            {modalContent === ModalState.failure && (
              <PopUp size={PopupSize.large}>
                <Warning
                  title={"Warning"}
                  text={handleWarningText()}
                  additionalText={t("modal_content.failure.additional_title", {
                    ns: "enumerations",
                  })}
                  iconWarning={true}
                />
                {handleTryAgain()}
              </PopUp>
            )}
            {modalContent === ModalState.noUser && (
              <PopUp size={PopupSize.large}>
                <ModalAuth
                  modalTitle={t(
                    "modal_content.user_does_not_exists.modal_title",
                    {
                      ns: "enumerations",
                    }
                  )}
                  additionalTitle={t(
                    "modal_content.user_does_not_exists.additional_title",
                    {
                      ns: "enumerations",
                    }
                  )}
                  logo={true}
                  primaryButton={t(
                    "modal_content.user_does_not_exists.primary_button",
                    { ns: "enumerations" }
                  )}
                  secondaryButton={t(
                    "modal_content.user_does_not_exists.secondary_button",
                    { ns: "enumerations" }
                  )}
                  primaryButtonOnClick={handleDisconnect}
                  secondaryButtonOnClick={handleSignup}
                ></ModalAuth>
              </PopUp>
            )}
            {modalContent === ModalState.noSocial && (
              <PopUp size={PopupSize.large}>
                <ModalAuth
                  modalTitle={t("ups", { ns: "common" })}
                  additionalTitle={t(
                    "enumerations:modal_content.user_does_not_have_social.modal_title_1"
                  )}
                  logo={true}
                  description={t(
                    "enumerations:modal_content.user_does_not_have_social.modal_title_2"
                  )}
                >
                  {handleCloseModal()}
                </ModalAuth>
              </PopUp>
            )}
          </>
        )}
      </div>
    </>
  );
};
